// checkout/utils/toggleFormRows.ts
import { toggleState } from '../../utils/toggleState';
import { toggleClass } from '../../utils/toggleClass';
import { lockFocusable, restoreFocusable } from './preserveTabindex';

const FOCUSABLE = 'input, select, textarea, button, a[href], [tabindex]';

/**
 * Toggles the visibility of a form__row class element based on the triggering element's data attributes.
 *
 * @param {HTMLElement | null} elem - The element that triggers the toggle action. It should have
 * `data-form-target` (CSS class of the target element) and optionally:
 * - `data-hide-on-click="true"` to hide the trigger after click
 * - `data-hide-parent-form-row="true"` to toggle the nearest parent .form__row
 * @returns {void}
 */
export function toggleFormRow(elem: HTMLElement | null): void {
	if (!elem) return;

	const dataFormTarget: string | undefined = elem.dataset.formTarget;
	const dataHideListener: string | undefined = elem.dataset.hideOnClick;
	const dataHideParentFormRow: string | undefined = elem.dataset.hideParentFormRow;

	const form = elem.closest('form') as HTMLFormElement | null;
	if (!form || !dataFormTarget) return;

	const targetElem = form.querySelector<HTMLElement>('.' + dataFormTarget);
	if (!targetElem) return;

	const focusables = targetElem.querySelectorAll<HTMLElement>(FOCUSABLE);

	// Will this action show the row?
	const willShow = targetElem.classList.contains('form__row--hidden');

	// If we're about to hide it, lock tabbing now and set aria-hidden.
	if (!willShow) {
		targetElem.setAttribute('aria-hidden', 'true');
		focusables.forEach((el) => lockFocusable(el));
		if (targetElem.contains(document.activeElement)) {
			(document.activeElement as HTMLElement).blur();
		}
	}

	// Toggle classes
	let classesToToggle: string[] = ['form__row--transition', 'form__row--hidden'];
	const hiddenClassIndex = classesToToggle.indexOf('form__row--hidden');
	toggleClass([targetElem], classesToToggle);
	if (hiddenClassIndex !== -1) classesToToggle.splice(hiddenClassIndex, 1);

	// Hide the trigger if requested (and mirror tabbability)
	if (dataHideListener === 'true') {
		toggleState([elem], 'hidden');
		const nowHidden = elem.classList.contains('hidden');
		elem.tabIndex = nowHidden ? -1 : 0;
		elem.toggleAttribute('aria-hidden', nowHidden);
	}

	// Hide the parent .form__row if requested (manage its tabbables too)
	if (dataHideParentFormRow === 'true') {
		const parentFormRow = elem.closest<HTMLElement>('.form__row');
		if (parentFormRow) {
			const parentWillShow = parentFormRow.classList.contains('form__row--hidden');
			toggleClass([parentFormRow], ['form__row--hidden']);

			const parentFocusables = parentFormRow.querySelectorAll<HTMLElement>(FOCUSABLE);
			if (parentWillShow) {
				parentFormRow.removeAttribute('aria-hidden');
				parentFocusables.forEach((el) => restoreFocusable(el));
			} else {
				parentFormRow.setAttribute('aria-hidden', 'true');
				parentFocusables.forEach((el) => lockFocusable(el));
				if (parentFormRow.contains(document.activeElement)) {
					(document.activeElement as HTMLElement).blur();
				}
			}
		}
	}

	// After transition: remove transition class and, if shown, restore tabbing + focus first
	targetElem.addEventListener(
		'transitionend',
		() => {
			setTimeout(() => {
				toggleClass([targetElem], classesToToggle);

				if (willShow) {
					targetElem.removeAttribute('aria-hidden');
					const shownFocusables = targetElem.querySelectorAll<HTMLElement>(FOCUSABLE);
					shownFocusables.forEach((el, index) => {
						restoreFocusable(el);
						if (index === 0) el.focus();
					});
				}
			}, 1);
		},
		{ once: true },
	);
}

/**
 * Lock tabbing for rows hidden on load.
 * @param {ParentNode} [root=document] - Scope to search.
 * @returns {void}
 */
export function initHiddenRows(root: ParentNode = document): void {
	root.querySelectorAll<HTMLElement>('.form__row--hidden').forEach((row) => {
		row.setAttribute('aria-hidden', 'true');
		row.querySelectorAll<HTMLElement>(FOCUSABLE).forEach((el) => {
			lockFocusable(el);
		});
	});
}
